import { useRef, useState, useCallback, useMemo, useEffect } from "react";
import { ReactComponent as IconInfoCircle } from "@/assets/svgs/infoCircle.svg";
import { ReactComponent as IconPlus } from "@/assets/svgs/icon-plus.svg";
import { ReactComponent as ArrowDown } from "@/assets/svgs/chevron-down.svg";
import { ReactComponent as DeleteIcon } from "@/assets/svgs/trash-outline-medium.svg";
import { ReactComponent as IconRefresh } from "@/assets/svgs/refresh.svg";
import { ReactComponent as IconPlusCircle } from "@/assets/svgs/icon-plus-circle.svg";
import { ReactComponent as IconDisabledPlusCircle } from "@/assets/svgs/icon-disabled-plus-circle.svg";
import { ReactComponent as ErrorInfoCircle } from "@/assets/svgs/exclamation-circle-solid-small.svg";
import { ReactComponent as XMark } from "@/assets/svgs/x-mark.svg";
import { ReactComponent as ErrorIcon } from "@/assets/svgs/etr-exclamation-triangle.svg";
import { InputDataTable } from "@/componentsDS/InputDataTable/index";
import Input from "@/componentsDS/Input/index";
import { uniqueId } from "lodash";
import PropTypes from "prop-types";
import { validateCustomHeaderKey, validateCustomHeaderValue } from "@/utils/custom-headers-validation";
import { CUSTOM_HEADER_ERROR_MESSAGES, MAX_CUSTOM_HEADERS } from "@/constants/custom-headers/index";

export const CustomHeadersTable = ({
	customHeadersData,
	setCustomHeadersData,
	customHeaderErrors,
	setCustomHeaderErrors,
	inputValues,
	setInputValues,
	httpHeaderKey,
	httpHeaderKeyError,
	authType
}) => {
	const dataTableRef = useRef(null);

	const processedCustomHeadersData = useMemo(() => {
		return customHeadersData.map(item => ({
			...item,
			id: item.id || uniqueId(`${item.key}_`)
		}));
	}, [customHeadersData]);

	const [isCustomHeadersTableOpen, setIsCustomHeadersTableOpen] =
		useState(true);

	// Filter duplicate errors with same key, remove the first one, keep rest
	const filterDuplicateKeyErrors = useCallback((newErrors) => {
		// filter http header key errors to show only the latest ones
		const httpHeaderKeyErrors = newErrors.filter(err => err.isHttpHeaderKey);
		if(httpHeaderKeyError && httpHeaderKeyErrors[0]?.unChangedValuekeyCount === 0) {
			if(httpHeaderKeyErrors.length === 1){
				newErrors = newErrors.filter(err => !err.isHttpHeaderKey);	
			}else{
				let oldestHttpHeaderKeyError = httpHeaderKeyErrors[0];
				newErrors = newErrors.filter(err => err?.cellId !== oldestHttpHeaderKeyError?.cellId);
			}
		}

		const samekeyErrorCount = {};
		const filteredErrors = [];

		newErrors.forEach((err) => {
			const key = err.key;
			if (err.errorMessage === "Key already exists") {
				samekeyErrorCount[key] = (samekeyErrorCount[key] || 0) + 1;
			} else {
				samekeyErrorCount[key] = 1;
			}
		});

		const seenErrors = {};
		newErrors.forEach((err) => {
			const key = err.key;
			/**
			 * if the count of same key error is 1, or the error exists in the unchanged part of the data as well
			 * do not filter any errors, show all of them, as the error wont be visible for the unchanged cell data
			 */
			if (
				samekeyErrorCount[key] === 1 || err.unChangedValuekeyCount > 0 || err.isHttpHeaderKey
			) {
				filteredErrors.push(err);
			} else if (!seenErrors[key]) {
				seenErrors[key] = true;
			} else {
				filteredErrors.push(err);
			}
		});

		return filteredErrors;
	}, [httpHeaderKeyError]);

	const validateCell = useCallback(
		(inputKey, newInputValue, rowId, updatedData) => {
			const cellId = `${rowId}_${inputKey}`;
			const trimmedValue = newInputValue ? newInputValue.trim() : "";
			let error = { cellId };

			if (inputKey === "key") {
				const headerKeyError = validateCustomHeaderKey(newInputValue);
				if (headerKeyError) {
					error.errorMessage = headerKeyError;
					return error;
				}

				const keyAlreadyExists = updatedData.filter(
					(item) => item.id !== rowId && item.key.trim() === trimmedValue
				);
				const isHttpHeaderKey = authType === "API_KEY" && httpHeaderKey && httpHeaderKey.toLowerCase() === trimmedValue.toLowerCase();

				if (keyAlreadyExists.length > 0 || isHttpHeaderKey) {
					error.errorMessage = CUSTOM_HEADER_ERROR_MESSAGES.KEY_ALREADY_EXISTS;
					error.key = trimmedValue;
					if(isHttpHeaderKey){
						error.isHttpHeaderKey = true;
					}
					// get count of same keys in the unchanged values of the table,
					// in order to show correct errors on the latest changes
					error.unChangedValuekeyCount = keyAlreadyExists.filter(
						(key) => !key.isChanged
					).length;
				}
			}

			if (inputKey === "value") {
				const headerValueError = validateCustomHeaderValue(newInputValue);
				if (headerValueError) {
					error.errorMessage = headerValueError;
					return error;
				}
			}

			return error.errorMessage ? error : null;
		},
		[httpHeaderKey, inputValues, authType]
	);

	const handleDataUpdate = useCallback((changedValues) => {
		const updatedData = processedCustomHeadersData.map((row) => {
			const changes = changedValues[row.id];
			if (changes) {
				return { ...row, ...changes, id: row.id };
			}
			return row;
		});

		const cleanedData = updatedData.map(({ key, value, id }) => ({ key, value, id }));
		setCustomHeadersData(cleanedData);

		validateChangedData(changedValues);
	}, [processedCustomHeadersData, setCustomHeadersData]);

	const validateChangedData = useCallback(
		(changedValues) => {
			let newErrors = [];

			// generate the updatedData with the changed values that contains table row related details
			const updatedData = processedCustomHeadersData.map((row) => {
				const changes = changedValues[row.id];
				return changes?.key ? { ...row, ...changes, isChanged: true } : row;
			});

			// Validate each cell of only the cells that are changed
			Object.entries(changedValues).forEach(([rowId, columns]) => {
				Object.entries(columns).forEach(([columnId, newInputValue]) => {
					const error = validateCell( columnId, newInputValue, rowId, updatedData );
					if (error) {
						newErrors.push(error);
					}
				});
			});

			// Validate new input values as well when table row data changes
			const inputKeyError =
				typeof inputValues["key"] === "string"
					? validateCell("key", inputValues["key"], "newKey", updatedData ) : null;
			const inputValueError =
				typeof inputValues["value"] === "string"
					? validateCell("value", inputValues["value"], "newValue", updatedData ) : null;

			if (inputKeyError) newErrors.push(inputKeyError);
			if (inputValueError) newErrors.push(inputValueError);

			const filteredErrors = filterDuplicateKeyErrors(newErrors);
			setCustomHeaderErrors(filteredErrors);
		},
		[processedCustomHeadersData, inputValues, validateCell, filterDuplicateKeyErrors, setCustomHeaderErrors]
	);

	// Set incoming data as changed values and validate (only on initial load)
	useEffect(() => {
		if (processedCustomHeadersData.length > 0 && dataTableRef?.current?.setChangedValues) {
			// Convert all existing data to changedValues format for validation
			const allDataAsChanges = {};
			processedCustomHeadersData.forEach((item) => {
				allDataAsChanges[item.id] = {
					key: item.key,
					value: item.value,
				};
			});

			dataTableRef.current.setChangedValues(allDataAsChanges);
			validateChangedData(allDataAsChanges);
		}
	}, []);

	// Validate any pending table changes when processedCustomHeadersData changes
	useEffect(() => {
		if (dataTableRef?.current?.changedValues) {
			validateChangedData(dataTableRef.current.changedValues);
		}
	}, [processedCustomHeadersData, validateChangedData]);

	// clear errors and key, value inside the new input fields
	const handleInputCancelClick = () => {
		setInputValues({});
		setCustomHeaderErrors((errors) => errors.filter((e) => e.cellId !== "newKey_key" && e.cellId !== "newValue_value"));
	};

	// add new input values to the table
	const handleInputSubmitClick = () => {
		setCustomHeadersData((prev) => [
			{
				key: inputValues.key.trim(),
				value: inputValues.value.trim(),
				id: uniqueId(`${inputValues.key}_`),
			},
			...prev,
		]);
		setInputValues({});
	};

	const deleteKey = useCallback((row) => {
		const tableRef = dataTableRef?.current;
	
		setCustomHeadersData((prev) => prev.filter((item) => item.id !== row.id));
		
		const updatedChangedValues = {
			...dataTableRef?.current?.changedValues,
		};
		delete updatedChangedValues[row.id]; 
		dataTableRef?.current?.setChangedValues(updatedChangedValues);
	
		const updatedRowSelection = { ...tableRef.rowSelection };
		delete updatedRowSelection[row.id];
		
		dataTableRef?.current?.setRowSelection(updatedRowSelection);
	}, []);

	const deleteSelectedKeys = useCallback(() => {
		const tableRef = dataTableRef?.current;
		let selectedRowIds = [];

		if (tableRef?.rowSelection) {
			selectedRowIds = Object.keys(tableRef.rowSelection).filter((key) => tableRef.rowSelection[key]);
		}

		setCustomHeadersData((prev) =>
			prev.filter((item) => !selectedRowIds.includes(item.id))
		);

		const updatedChangedValues = {
			...dataTableRef?.current?.changedValues,
		};

		selectedRowIds.forEach((rowId) => {
			delete updatedChangedValues[rowId];
		});

		dataTableRef?.current?.setChangedValues(updatedChangedValues);
		dataTableRef?.current?.setRowSelection({});
	}, [setCustomHeadersData]);

	const toggleCustomHeadersDataTable = () => {
		setIsCustomHeadersTableOpen((isCustomHeadersTableOpen) => !isCustomHeadersTableOpen);
	};

	const onInputChange = useCallback((e, inputKey) => {
		const updatedData = processedCustomHeadersData.map((row) => {
			const changes = dataTableRef?.current?.changedValues[row.id];
			return changes ? { ...row, ...changes } : row;
		});

		const inputValue = inputKey === "key" ? e.target.value.trim() : e.target.value;

		const error = validateCell( inputKey, inputValue, inputKey === "key" ? "newKey" : "newValue", updatedData);

		if (error) {
			setCustomHeaderErrors((prev) => [...prev, error]);
		} else {
			setCustomHeaderErrors((prev) =>
				prev.filter( (e) => e.cellId !== (inputKey === "key" ? "newKey_key" : "newValue_value") )
			);
		}

		if (inputKey === "key" || inputKey === "value") {
			setInputValues((prev) => ({ ...prev, [inputKey]: inputValue, }));
		}
	}, [processedCustomHeadersData, validateCell, setCustomHeaderErrors]);

	// clear errors and key, value inside the new input fields
	useEffect(() => {
		const keyEmpty = !inputValues?.key || inputValues.key.trim().length === 0;
		const valueEmpty = !inputValues?.value || inputValues.value.trim().length === 0;
	
		// Only clear if BOTH are empty AND no partial typing is going on
		if (keyEmpty && valueEmpty) {
			// Only reset if it's already empty (i.e., not just one field starting to be typed)
			if (inputValues.key !== undefined || inputValues.value !== undefined) {
				setCustomHeaderErrors((prev) =>
					prev.filter((e) => e.cellId !== "newKey_key" && e.cellId !== "newValue_value")
				);
				setInputValues({});
			}
		}
	}, [inputValues]);

	const isMaxLimitReached = useMemo(
		() => processedCustomHeadersData.length >= MAX_CUSTOM_HEADERS,
		[processedCustomHeadersData.length]
	);

	const isSubmitDisabled = useMemo(() => {
		const keyEmpty = !inputValues.key || inputValues.key.trim().length === 0;
		const valueEmpty = !inputValues.value || inputValues.value.trim().length === 0;
		const hasKeyError = customHeaderErrors.some((error) => error.cellId === "newKey_key");
		const hasValueError = customHeaderErrors.some((error) => error.cellId === "newValue_value");

		return keyEmpty || valueEmpty || hasKeyError || hasValueError;
	}, [inputValues, customHeaderErrors]);

	const renderSelectHeader = useCallback(({ table }) => (
		<label className="custom-headers__data-table__table__body__content--items_checkbox" aria-label="Select row">
			<input
				type="checkbox"
				data-testid="custom-headers-select-all"
				checked={table.getIsAllRowsSelected()}
				ref={(input) => {
					if (input) input.indeterminate = table.getIsSomeRowsInPageSelected();
				}}
				disabled={table?.options?.meta?.editMode}
				onChange={(e) => {
					e.stopPropagation();
					table.getToggleAllRowsSelectedHandler()({
						target: { value: e.target.checked },
					});
				}}
			/>
			<span className="checkmark" />
		</label>
	), []);

	const renderSelectCell = useCallback(({ row, table }) => {
		return (
			<label className="custom-headers__data-table__table__body__content--items_checkbox" aria-label="Select row">
				<input
					type="checkbox"
					data-testid="custom-headers-select"
					checked={row.getIsSelected()}
					disabled={!row.getCanSelect() || table?.options?.meta?.editMode}
					onChange={(e) => {
						e.stopPropagation();
						row.getToggleSelectedHandler()({
							target: { value: e.target.checked },
						});
					}}
				/>
				<span className="checkmark" />
			</label>
		);
	}, []);

	const renderSelectInput = () => (
		<div
			className={isMaxLimitReached ? "custom-headers-input-icon--disabled" : "custom-headers-input-icon"}
		>
			<IconPlus />
		</div>
	);

	const renderMaxLimitError = () => (
		<div className="custom-headers__max-limit-wrapper">
			<div>
				<ErrorIcon
					className="custom-headers__max-limit-icon"
					data-testid="max-limit-error-icon"
				/>
			</div>
			<div className="custom-headers__error-message">
				Maximum key limit reached. You may either update or remove existing keys to add a new key.
			</div>
		</div>
	);

	const clearNewInputField = (inputKey) => {
		setInputValues((prev) => ({ ...prev, [inputKey]: "" }));
		setCustomHeaderErrors((prev) =>
			prev.filter((e) => e.cellId !== (inputKey === "key" ? "newKey_key" : "newValue_value"))
		);
	};

	const renderInputField = useCallback((inputKey, placeholder) => {
		if (isMaxLimitReached && inputKey === "key") {
			return renderMaxLimitError();
		}
		if (isMaxLimitReached && inputKey === "value") {
			return null;
		}

		const errorKey = inputKey === "key" ? "newKey_key" : "newValue_value";
		const error = customHeaderErrors.find((error) => error.cellId === errorKey);

		const ifSiblingHasError = customHeaderErrors.find(
			(error) => (error.cellId === "newKey_key") | (error.cellId === "newValue_value")
		);

		const handleKeyDown = (e) => {
			if (e.key === 'Enter') {
				e.preventDefault();
				e.stopPropagation();
			}
		};

		return (
			<div
				style={{
					width: "100%",
					display: "flex",
					flexDirection: "column",
					padding: "10px 4px",
				}}
			>
				<Input
					data-testid={`input-enter-new-${inputKey}`}
					className={ifSiblingHasError ? "custom-headers__create-input-error" : "custom-headers__create-input"}
					placeholder={placeholder}
					type="text"
					value={inputValues?.[inputKey] ?? ""}
					onChange={(e) => onInputChange(e, inputKey)}
					cancelIcon={<XMark data-testid={`cancel-${inputKey}-input`} />}
					handleOnCancel={() => clearNewInputField(inputKey)}
					onKeyDown={handleKeyDown}
				/>
				{error?.errorMessage && (
					<div className="DS__error__wrapper">
						<ErrorInfoCircle
							className="DS__error__icon"
							data-testid="error-info-icon"
						/>
						<div className="DS__error__message">
							{error.errorMessage}
						</div>
					</div>
				)}
			</div>
		);
	}, [isMaxLimitReached, customHeaderErrors, inputValues, onInputChange, clearNewInputField, renderMaxLimitError]);

	const renderActionCell = useCallback((table) => (
		<div
			style={{
				display: "flex",
				justifyContent: "right",
				width: "100%",
				paddingRight: "12px",
				paddingTop: "14px",
			}}
		>
			<div
				className="custom-headers__delete-row-icon"
				onClick={() => deleteKey(table.row.original)}
				role="none" 
				onKeyDown={()=>{}}
			>
				<DeleteIcon />
			</div>
		</div>
	), [deleteKey]);

	const renderActionInput = () => {
		if (isMaxLimitReached) {
			return null;
		}

		return (
			<div className="custom-header__action">
				<div
					data-testid="data_table__input-cancel-add-custom-headers"
					className="custom-header__refresh-main custom-headers__interactive tooltip"
					onClick={handleInputCancelClick}
					role="none" 
					onKeyDown={()=>{}}
				>
					<div className="tooltiptext custom-headers-tooltip-text">
						Reset
					</div>
					<IconRefresh data-testid="close-icon"/>
				</div>
				<div
					data-testid="data_table__input-submit-add-custom-headers"
					className={`custom-header__submit-icon ${
						isSubmitDisabled ? "data_table__input__action__button--disabled" : "custom-headers__interactive"
					}`}
					onClick={isSubmitDisabled ? () => {} : handleInputSubmitClick}
					role="none" 
					onKeyDown={()=>{}}
				>
					{isSubmitDisabled ? (
						<IconDisabledPlusCircle data-testid="plus-icon" className="custom-headers__submit-icon custom-headers__submit-icon--disabled" />
					) : (
						<IconPlusCircle data-testid="plus-icon" className="custom-headers__submit-icon" />
					)}
				</div>
			</div>
		);
	};

	// Column configuration
	const customHeadersColumn = useMemo(
		() => [
			{
				id: "select",
				size: 55,
				maxSize: 55,
				enableResizing: false,
				header: renderSelectHeader,
				cell: renderSelectCell,
				input: {
					type: "text",
					render: renderSelectInput,
				},
			},
			{
				id: "key",
				header: "Key",
				sortable: true,
				enableResizing: false,
				accessorKey: "key",
				minSize: 500,
				flex: 0.5,
				input: {
					type: "text",
					required: true,
					render: () => renderInputField("key", "Enter New Key"),
				},
				edit: {
					type: "input",
					customOnChange: (value) => value.trim(),
				},
				editable: true,
			},
			{
				id: "value",
				header: "Value",
				sortable: true,
				enableResizing: false,
				accessorKey: "value",
				minSize: 500,
				flex: 0.5,
				input: {
					type: "text",
					required: true,
					render: () => renderInputField("value", "Enter New Value"),
				},
				edit: {
					type: "input",
				},
				editable: true,
			},
			{
				id: "action",
				header: "",
				sortable: false,
				enableResizing: false,
				size: 100,
				cell: renderActionCell,
				input: {
					type: "actions",
					render: renderActionInput,
				},
			},
		],
		[
			renderSelectHeader,
			renderSelectCell,
			renderSelectInput,
			renderInputField,
			renderActionCell,
			renderActionInput,
		]
	);

	return (
		<div
			className={`custom-headers-table ${
				!isCustomHeadersTableOpen
					? "custom-headers-table--collapsed"
					: ""
			}`}
		>
			<div
				className="custom-headers-table__header"
				onClick={toggleCustomHeadersDataTable}
				role="none" 
				onKeyDown={()=>{}}
			>
				<ArrowDown
					fontSize={16}
					className={`custom-headers-table__icon ${
						isCustomHeadersTableOpen
							? "custom-headers-table__icon--open"
							: ""
					}`}
				/>
				<span className="custom-headers-table__title">
					Custom Headers
				</span>
			</div>
			<></>
			{isCustomHeadersTableOpen && (
				<>
					<div className="flex-box flex-direction-row flex-align-center custom-headers-table__info">
						<IconInfoCircle />
						<div className="text-normal custom-headers-table__description">
							Up to 10 keys only. At least one custom header required.
						</div>
					</div>
					<InputDataTable
						ref={dataTableRef}
						data={processedCustomHeadersData}
						columns={customHeadersColumn}
						showPageSize={true}
						showInput
						showSelectedActions={false}
						isEditable={true}
						meta={{
							onDataUpdate: handleDataUpdate,
						}}
						errors={customHeaderErrors}
						selectedHeaderActions={
							<div
								className="DS__delete-selection"
								onClick={deleteSelectedKeys}
								role="none" 
								onKeyDown={()=>{}}
							>
								Delete Selection
							</div>
						}
					/>
				</>
			)}
		</div>
	);
};

CustomHeadersTable.propTypes = {
	customHeadersData: PropTypes.array,
	setCustomHeadersData: PropTypes.func,
	customHeaderErrors: PropTypes.array,
	setCustomHeaderErrors: PropTypes.func,
	inputValues: PropTypes.object,
	setInputValues: PropTypes.func,
	httpHeaderKey: PropTypes.string,
	httpHeaderKeyError: PropTypes.string,
	authType: PropTypes.string,
};